import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { z } from 'zod';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Helper function to get the description of a field in a Zod schema
 * @param schema - The Zod schema to extract description from
 * @param path - Optional dot-notation path to a nested field (e.g., 'fl3xx.flight')
 * @returns The description string or undefined if not found
 */
export function getSchemaDescription(
  schema: z.ZodTypeAny,
  path?: string,
): string | undefined {
  // If schema doesn't exist, return undefined
  if (!schema) {
    return undefined;
  }

  // If no path is provided, return the description of the schema itself
  if (!path) {
    return schema._def.description;
  }

  // Split the path into segments
  const segments = path.split('.');
  let currentSchema = schema;

  // Traverse the schema based on the path
  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i];

    // Make sure we're dealing with an object that has a shape
    if (!(currentSchema instanceof z.ZodObject)) {
      // Try to handle ZodEffects and other wrapper types that might contain an inner schema
      if ('innerType' in currentSchema._def && currentSchema._def.innerType) {
        currentSchema = currentSchema._def.innerType;
        // Retry with the inner type
        i--; // Decrease i to reprocess the current segment with the inner type
        continue;
      }

      return undefined;
    }

    // Try to get the next schema level
    try {
      currentSchema = currentSchema.shape[segment];
    } catch (e) {
      return undefined;
    }

    // If the segment doesn't exist in the schema, return undefined
    if (!currentSchema) {
      return undefined;
    }
  }

  // Return the description of the final schema
  const finalDescription = currentSchema._def?.description;
  return finalDescription;
}
